import { useEffect } from 'react';
import type { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';

import g from 'src/constants/urls';
import { IArticleByGroupDetail } from 'src/interface/ArticleByGroup';
import wrapper from 'src/redux';

import { decryptDLoginBE } from '@/utils/func/encrypt';
import { gtmTrackEvent } from '@/utils/gtmTrackEvent';

const HeadC = dynamic(() => import('src/components/Head'), {ssr: true});
const FaqContainer = dynamic(() => import('src/containers/FaqLandingPage'), {ssr: true});
const HeroContainer = dynamic(() => import('src/containers/Hero'), {ssr: true});
const GenerateAi = dynamic(() => import('src/containers/GenerateAi'), {ssr: true});
const GenerateJobsForBaby = dynamic(() => import('src/containers/GenerateJobsForBaby'), {ssr: true});
// const JourneyContainer = dynamic(() => import('src/containers/Journey'));
const JourneyContainerNew = dynamic(() => import('src/containers/JourneyNew'), {ssr: true});
// const ComingSoonContainer = dynamic(() => import('src/containers/ComingSoon'));
const TestimoniContainer = dynamic(() => import('src/containers/Testimoni'), {ssr: true});
const ReadArticleContainer = dynamic(() => import('src/containers/ReadArticle'), {ssr: true});
const PromoContainer = dynamic(() => import('src/containers/Promo'), {ssr: true});
const EventHomeContainer = dynamic(() => import('src/containers/EventHome'), {ssr: true});
const JoinKomunitasBerandaContainer = dynamic(() => import('src/containers/JoinKomunitasBeranda'), {ssr: true});
const DownloadContainer = dynamic(() => import('src/containers/Download'), {ssr: true});
const HomeJsonLd = dynamic(() => import('src/components/Layout/JsonLd/Home'), {ssr: true});

const BrandaPage = (props: any) => {
	const router = useRouter();

	useEffect(() => {
		if (!router.isReady) return;

		const from = router.query.from;
		const dlogin = Cookies.get('d_login');

		let userData = null;

		if (typeof dlogin === 'string') {
			userData = decryptDLoginBE(dlogin);
		}

		let formattedDate = null;
		if (userData?.birthdate) {
			const d = new Date(userData.birthdate);
			if (!isNaN(d.getTime())) {
			formattedDate = d.toISOString().split('T')[0];
			}
		}

		let eventName = '';
		if (from === 'login') {
			eventName = 'login_success';
		} else if (from === 'register') {
			eventName = 'register_success';
		}

		if (!eventName || !userData) return;

		setTimeout(() => {
			gtmTrackEvent({
				event: eventName,
				uid: userData.uid,
				email: userData.email,
				birthdate: formattedDate,
				name: userData.name,
				phone_number: userData.phone_number,
				weight: userData.weight,
				height: userData.height,
				referral: userData.referal_code,
				phase: userData.web_phase_type,
			});
		}, 500);

	}, [router.isReady, router.query.from]);

	return (
		<>
			<HeadC
				title="Hallobumil #MengertiMama - Aplikasi Kehamilan & Parenting Mama"
				description={
					'Bareng HalloBumil, perjalanan Mama akan lebih asyik. ' +
					'Dapatkan semua yang Mama butuhin, dari tips kece sampai komunitas seru!'
				}
			/>
			<HeroContainer />
			{/* <JourneyContainer /> */}
			<JourneyContainerNew />
			<ReadArticleContainer {...props} />
			{
				props.promo.length === 0 ? ''
				:
				<PromoContainer {...props} />
			}
			<GenerateJobsForBaby />
			<GenerateAi />
			<EventHomeContainer {...props} />
			<JoinKomunitasBerandaContainer />
			{/* <ComingSoonContainer /> */}
			<TestimoniContainer {...props} />
			<FaqContainer {...props} />
			<DownloadContainer />
			<HomeJsonLd />
		</>
	);
};

export const getStaticProps: GetStaticProps = wrapper.getStaticProps((store) => async () => {
	let isError: boolean = false;
	let faq: any = [];
	let dataArtikel: any = [];
	let testimoni: any = [];
	let promo: any = [];
	let event: any = [];

	const queryClient = new QueryClient();

	try {
		const uri = `${g.API_URL}/public/hb-faq?category_id=2`;
		const opts = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		};
		const faqList = await fetch(uri, opts);
		faq = await faqList.json().then((res) => ({ ...res, status: faqList.status }));

		const uriArtikel = `${g.API_ARTICLE}/all?order=desc&orderby=publish_date&per_page=9&page=1`;
		const opts2 = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		};

		const artikelList = await fetch(uriArtikel, opts2);
		dataArtikel = await artikelList.json().then((res) => ({ ...res, status: artikelList.status }));

		const uripromo = `${g.API_URL}/public/brand-placement`;
		const optsPromo = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		};
		const promoList = await fetch(uripromo, optsPromo);
		promo = await promoList.json().then((res) => ({ ...res, status: promoList.status }));

		const urievent = `${g.API_EVENT}/events/list?Sort=start_date&Dir=desc`;
		const optsEvent = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		};
		const eventList = await fetch(urievent, optsEvent);
		event = await eventList.json().then((res) => ({ ...res, status: eventList.status }));

		const urlTestimoni = `${g.API_CONTACT_US}/testimony?sort=contact_name&dir=&category=general`;
		const optsTestimoni = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		};

		const testimoniList = await fetch(urlTestimoni, optsTestimoni);
		testimoni = await testimoniList
			.json()
			.then((res) => ({ ...res, status: testimoniList.status }));
	} catch (err: any) {
		if (err?.status === 400) {
			isError = true;
		} else {
			throw err;
		}
	}

	const article: IArticleByGroupDetail[] =
		store.getState().articleByGroup.articleByCategory?.data?.records;

	if (!article && isError) return { notFound: true };
	return {
		props: {
			dehydratedState: dehydrate(queryClient),
			card: true,
			isWhiteBackground: true,
			JsonLd: {
				organization: true,
				breadcrumbs: true,
			},
			pageHeaderContent: {
				title: 'pra kehamilan',
				breadcrumbs: [
					{ label: 'Artikel', navigatePath: '/artikel' },
					{ label: 'Pra Kehamilan', navigatePath: '/pre-pregnancy' },
				],
			},
			faq: faq.data.records || [],
			artikel: dataArtikel.data.records || [],
			testimoni: testimoni.data.records || [],
			promo: promo.data.records || [],
			event: event.data.records || [],
		},
		revalidate: 60,
	};
});

export default BrandaPage;
